import { Action } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { of, Observable } from 'rxjs';
import {
  map, first, mergeMap, ignoreElements, tap, filter,
} from 'rxjs/operators';

import { ls } from '@ezugi/utils';

import { mediaActions, settingsActions } from '../../actions';
import { defaultSettings, SETTINGS_KEY } from '../../constants/settings';
import { setMute as chatSetMute } from '../../reducers/chat';
import { isStartGameMutedSelector, settingsSelector, userIdSelector } from '../../selectors';
import { RootState } from '../../types';

const { settings } = settingsActions;
const { sound, music, videoStreamSound } = mediaActions;

const getUserSettingsKey = (state: RootState) => `${SETTINGS_KEY}_${userIdSelector(state)}`;

const applySettingsEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(settings.update.match),
  map(() => {
    const [ err ] = ls.put(getUserSettingsKey(state$.value), settingsSelector(state$.value as any) as any);
    return err ? settings.failure() : settings.success();
  })
);

const initSettingsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(settings.init.match),
  first(),
  mergeMap(() => {
    const [ err, savedSettings ] = ls.get(getUserSettingsKey(state$.value));

    const currentSettings = err ? defaultSettings : savedSettings;
    const shouldStartGameMuted = isStartGameMutedSelector(state$.value);
    return of(
      chatSetMute(currentSettings.hideOthersChat),
      sound.set(currentSettings.gameSounds),
      music.set(currentSettings.backgroundMusic),
      videoStreamSound.set(shouldStartGameMuted ? false : currentSettings.videoStreamSound)
    );
  }),
);

const clearSettingsEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(settings.clear.match),
  tap(() => {
    const [ err, settingsCache ] = ls.search(new RegExp(SETTINGS_KEY));

    if (!err && settingsCache) {
      const userKey = getUserSettingsKey(state$.value);

      settingsCache.forEach(({ key }) => {
        if (key !== userKey) {
          ls.del(key);
        }
      });
    }
  }),
  ignoreElements()
);

export default combineEpics(applySettingsEpic, initSettingsEpic, clearSettingsEpic);
